import axios, { AxiosRequestConfig } from "axios";
import { toast } from "../utils/toast";

export interface ApiRequestConfig extends AxiosRequestConfig {
  skipErrorToast?: boolean;
  skipSuccessToast?: boolean;
}

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});

const getMethod = (config?: AxiosRequestConfig) =>
  (config?.method || "get").toLowerCase();

const getUrl = (config?: AxiosRequestConfig) => config?.url || "";

/** Auth login/register: keep errors inline on the page; leave success silent. */
const isSilentAuthEndpoint = (config?: AxiosRequestConfig) => {
  const url = getUrl(config);
  return url.includes("/auth/login") || url.includes("/auth/register");
};

const shouldSkipSuccessToast = (config?: ApiRequestConfig) => {
  if (!config) return false;
  if (config.skipSuccessToast) return true;
  if (getMethod(config) === "get") return true;
  if (isSilentAuthEndpoint(config)) return true;
  return false;
};

const shouldSkipErrorToast = (config?: ApiRequestConfig) => {
  if (!config) return false;
  if (config.skipErrorToast) return true;
  // Load failures stay inline (Alert) on the page
  if (getMethod(config) === "get") return true;
  if (isSilentAuthEndpoint(config)) return true;
  return false;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: mutation success/error toasts (top-right)
api.interceptors.response.use(
  (response) => {
    const config = response.config as ApiRequestConfig;
    if (!shouldSkipSuccessToast(config) && response.data?.message) {
      toast.notifySuccess(response.data.message);
    }
    return response;
  },
  (error) => {
    const { response, config, request } = error;
    const apiConfig = config as ApiRequestConfig | undefined;
    const skipToast = shouldSkipErrorToast(apiConfig);
    const silentAuth = isSilentAuthEndpoint(apiConfig);

    if (response) {
      switch (response.status) {
        case 400:
          if (!skipToast) {
            if (response.data?.errors) {
              response.data.errors.forEach(
                (err: { msg?: string; message?: string }) => {
                  toast.notifyError(err.msg || err.message || "Validation error");
                }
              );
            } else {
              toast.notifyError(response.data?.message || "Bad request");
            }
          }
          break;
        case 401:
          if (silentAuth) {
            // Login/register failures: Redux Alert on the page
            break;
          }
          if (!skipToast) {
            toast.notifyError("Unauthorized access. Please login again.");
          }
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          if (!window.location.pathname.startsWith("/login")) {
            window.location.href = "/login";
          }
          break;
        case 403:
          if (!skipToast) {
            toast.notifyError(
              "You do not have permission to perform this action."
            );
          }
          break;
        case 404:
          if (!skipToast) {
            toast.notifyError(response.data?.message || "Resource not found");
          }
          break;
        case 429:
          if (!skipToast) {
            toast.notifyError(
              "Too many requests. Please wait a moment and try again."
            );
          }
          break;
        case 500:
          if (!skipToast) {
            toast.notifyError(
              response.data?.message ||
                "Internal server error. Please try again later."
            );
          }
          break;
        default:
          if (!skipToast) {
            toast.notifyError(response.data?.message || "Something went wrong");
          }
      }
    } else if (!skipToast) {
      if (request) {
        toast.notifyError("Network error. Please check your connection.");
      } else {
        toast.notifyError("An unexpected error occurred.");
      }
    }

    return Promise.reject(error);
  }
);

export default api;
